import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Autocomplete,
  TextField,
  Switch,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
  generateAndPrintEndOfDay,
  EndOfDayPrintFilters,
  EndOfDayServiceContext,
} from '../services/endOfDayDataService';

interface EndOfDayPrintModalProps {
  open: boolean;
  onClose: () => void;
  /** Manager modu: kendi şubesine kilitli */
  isManager?: boolean;
  managerBranchId?: string;
  /**
   * ✅ YENİ: Hızlı satış / restoran ekranına atanmış şube.
   * Boş/null değilse, modal şube filtresi bu şubeye kilitlenir
   * (kasiyer sadece çalıştığı ekranın şubesinin gün sonunu alabilir).
   */
  lockedBranchId?: string | null;
}

const EndOfDayPrintModal: React.FC<EndOfDayPrintModalProps> = ({
  open,
  onClose,
  isManager = false,
  managerBranchId,
  lockedBranchId,
}) => {
  const { t } = useTranslation('salesSystem');
  const { branches, getBranchName, userDetails } = useAuth();
  const companyId = userDetails?.companyId || '';

  // ✅ Şube kilidi: ekran-atamasi (lockedBranchId) veya manager modu
  // Geçerli (gerçek) bir şubeye kilitleyebilmek için branches içinde bulunmalı.
  const effectiveLockedBranchId =
    lockedBranchId && branches.some(b => b.id === lockedBranchId)
      ? lockedBranchId
      : isManager && managerBranchId
        ? managerBranchId
        : null;
  const isBranchLocked = !!effectiveLockedBranchId;

  const [startDate, setStartDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => new Date());
  const [selectedBranches, setSelectedBranches] = useState<string[]>(() => {
    if (effectiveLockedBranchId) {
      return [effectiveLockedBranchId];
    }
    return [];
  });
  const [filterFiscalOnly, setFilterFiscalOnly] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ✅ Modal her açıldığında / kilitli şube değiştiğinde seçimi senkronla
  React.useEffect(() => {
    if (!open) return;
    if (effectiveLockedBranchId) {
      setSelectedBranches([effectiveLockedBranchId]);
    }
  }, [open, effectiveLockedBranchId]);

  const handlePrint = async () => {
    if (!startDate || !endDate) {
      setError(t('endOfDayPrintModal.errors.selectDates', 'Lütfen başlangıç ve bitiş tarihi seçin'));
      return;
    }
    if (!companyId) {
      setError(t('endOfDayPrintModal.errors.noCompany', 'Şirket bilgisi bulunamadı'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters: EndOfDayPrintFilters = {
        startDate,
        endDate,
        branchIds: selectedBranches,
        filterFiscalOnly,
      };
      const ctx: EndOfDayServiceContext = {
        branches: branches.map(b => ({ id: b.id, name: b.name })),
        getBranchName: (id: string) => getBranchName(id) || '-',
        companyId,
        // Hem manager hem de "ekrana atanmış şube" senaryolarında
        // servis tarafı isManager mantığı ile kilidi uygular.
        isManager: isBranchLocked || isManager,
        managerBranchId: effectiveLockedBranchId || managerBranchId,
      };

      await generateAndPrintEndOfDay(filters, ctx);

      onClose();
    } catch (err: any) {
      console.error('[EndOfDayPrintModal] Yazdırma hatası:', err);
      setError(err?.message || t('endOfDayPrintModal.errors.printFailed', 'Rapor hazırlanırken hata oluştu'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon color="primary" />
          <Typography variant="h6" component="span" fontWeight="bold">
            {t('endOfDayPrintModal.title', 'Gün Sonu Raporu Yazdır')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            disabled={loading}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ position: 'relative', pb: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label={t('endOfDayPrintModal.startDate', 'Başlangıç Tarihi')}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  format="dd.MM.yyyy HH:mm"
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label={t('endOfDayPrintModal.endDate', 'Bitiş Tarihi')}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  format="dd.MM.yyyy HH:mm"
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  size="small"
                  limitTags={3}
                  disabled={isBranchLocked || loading}
                  options={
                    isBranchLocked
                      ? branches.filter(b => b.id === effectiveLockedBranchId)
                      : branches.filter(b => b.id !== 'null')
                  }
                  getOptionLabel={(option) => option.name}
                  value={branches.filter(b => selectedBranches.includes(b.id))}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, newValue) => {
                    if (isBranchLocked) return;
                    setSelectedBranches(newValue.map(v => v.id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        isBranchLocked
                          ? t('endOfDayPrintModal.screenBranch', 'Ekran Şubesi')
                          : t('endOfDayPrintModal.branch', 'Şube')
                      }
                      placeholder={
                        selectedBranches.length === 0
                          ? t('endOfDayPrintModal.allBranches', 'Tüm Şubeler')
                          : undefined
                      }
                      helperText={
                        isBranchLocked
                          ? t('endOfDayPrintModal.branchLocked', 'Şube seçimi kilitli')
                          : selectedBranches.length === 0
                            ? t('endOfDayPrintModal.allBranchesSelected', 'Tüm şubeler seçili')
                            : undefined
                      }
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* ÖKC Filtresi (yarı görünür - hover ile tam görünür) */}
            <Box sx={{ position: 'absolute', right: -4, bottom: -4 }}>
              <Switch
                size="small"
                checked={filterFiscalOnly}
                onChange={(e) => setFilterFiscalOnly(e.target.checked)}
                disabled={loading}
                sx={{ opacity: 0.15, '&:hover': { opacity: 1 }, transition: 'opacity 0.3s' }}
              />
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            {t(
              'endOfDayPrintModal.info',
              'Filtrele ve Yazdır butonuna basıldığında rapor hazırlanıp doğrudan yazdırma penceresine gönderilir.',
            )}
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            {t('endOfDayPrintModal.cancel', 'İptal')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrint}
            disabled={loading || !startDate || !endDate}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FilterListIcon />}
          >
            {loading
              ? t('endOfDayPrintModal.preparing', 'Hazırlanıyor...')
              : t('endOfDayPrintModal.filterAndPrint', 'Filtrele ve Yazdır')}
          </Button>
        </DialogActions>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EndOfDayPrintModal;
